import React, { useState, useEffect } from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { Avatar, AvatarImage, AvatarFallback } from '../ui/avatar';
import {
  Send,
  Loader2,
  MessageSquare,
  RefreshCw,
  User,
  Shield
} from 'lucide-react';
import { agentAPI } from '../../services/api';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';

export default function StudentNotesTab({ studentId }) {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newNote, setNewNote] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchNotes();
  }, [studentId]);

  const fetchNotes = async () => {
    try {
      setLoading(true);
      const response = await agentAPI.getStudentNotes(studentId);
      setNotes(response.notes || []);
    } catch (error) {
      console.error('Failed to fetch notes:', error);
      toast.error('Failed to load notes');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitNote = async (e) => {
    e.preventDefault();
    if (!newNote.trim()) {
      toast.error('Please enter a note');
      return;
    }

    setSubmitting(true);
    try {
      await agentAPI.addStudentNote(studentId, newNote.trim());
      toast.success('Note added successfully');
      setNewNote('');
      fetchNotes();
    } catch (error) {
      console.error('Failed to add note:', error);
      toast.error(error.response?.data?.error || 'Failed to add note');
    } finally {
      setSubmitting(false);
    }
  };

  const getRoleBadge = (role) => {
    switch (role) {
      case 'super_admin':
        return (
          <Badge className="bg-purple-100 text-purple-700 text-xs">
            <Shield className="w-3 h-3 mr-1" />
            Super Admin
          </Badge>
        );
      case 'agent':
        return (
          <Badge className="bg-blue-100 text-blue-700 text-xs">
            <User className="w-3 h-3 mr-1" />
            Agent
          </Badge>
        );
      case 'counselor':
        return (
          <Badge className="bg-green-100 text-green-700 text-xs">
            <User className="w-3 h-3 mr-1" />
            Counselor
          </Badge>
        );
      default:
        return null;
    }
  };

  const getAvatarColor = (role) => {
    switch (role) {
      case 'super_admin':
        return 'from-purple-400 to-purple-600';
      case 'agent':
        return 'from-blue-400 to-blue-600';
      case 'counselor':
        return 'from-green-400 to-green-600';
      default:
        return 'from-gray-400 to-gray-600';
    }
  };

  return (
    <div className="space-y-6">
      {/* Add Note Form */}
      <Card className="p-4 bg-gray-50/50 border-gray-200">
        <form onSubmit={handleSubmitNote}>
          <div className="space-y-3">
            <div className="flex items-center gap-2 mb-2">
              <MessageSquare className="w-4 h-4 text-gray-500" />
              <h4 className="font-medium text-gray-900">Add a Note</h4>
            </div>
            <Textarea
              value={newNote}
              onChange={(e) => setNewNote(e.target.value)}
              placeholder="Write a note about this student for the Super Admin..."
              rows={3}
              className="resize-none"
            />
            <div className="flex justify-end">
              <Button
                type="submit"
                disabled={submitting || !newNote.trim()}
                className="bg-emerald-500 hover:bg-emerald-600"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Send Note
                  </>
                )}
              </Button>
            </div>
          </div>
        </form>
      </Card>

      {/* Notes List */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Conversation History</h3>
          <Button
            variant="ghost"
            size="sm"
            onClick={fetchNotes}
            disabled={loading}
          >
            <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : notes.length === 0 ? (
          <Card className="p-8 text-center bg-gray-50/50">
            <MessageSquare className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <h4 className="text-gray-700 font-medium mb-1">No notes yet</h4>
            <p className="text-sm text-gray-500">
              Add a note to start the conversation with Super Admin
            </p>
          </Card>
        ) : (
          <div className="space-y-4">
            {notes.map((note) => (
              <Card
                key={note.noteId}
                className={`p-4 ${
                  note.authorRole === 'super_admin'
                    ? 'bg-purple-50/50 border-purple-200'
                    : 'bg-blue-50/50 border-blue-200'
                }`}
              >
                <div className="flex gap-3">
                  <Avatar className="h-10 w-10 flex-shrink-0">
                    <AvatarImage src={note.author?.avatar} />
                    <AvatarFallback className={`text-white font-semibold bg-gradient-to-br ${getAvatarColor(note.authorRole)}`}>
                      {note.author?.firstName?.[0]}{note.author?.lastName?.[0]}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className="font-medium text-gray-900">
                        {note.author?.firstName} {note.author?.lastName}
                      </span>
                      {getRoleBadge(note.authorRole)}
                      <span className="text-xs text-gray-500">
                        {formatDistanceToNow(new Date(note.createdAt), { addSuffix: true })}
                      </span>
                    </div>
                    <p className="text-gray-700 whitespace-pre-wrap">{note.text}</p>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
